import { useContext, useEffect } from "react";
import { ImInfinite } from "react-icons/im";
import { MdDashboardCustomize, MdSecurity } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api";
import Button from "../../components/Button";
import Header from "../../components/Header";
import { AuthContext } from "../../context/AuthContext";
import { LoaderContext } from "../../context/LoaderContext";
import { useToken } from "../../helper/hooks";
import FeatureItem from "./components/FeatureItem";
import PricingItem from "./components/PricingCard";
import {
  StyledHeroSection,
  StyledPricingPlans,
  StyledPrivacySection,
  StyledSection,
  StyledSectionTitle,
} from "./styled";
import DemoItem from "./components/DemoItem";
import Footer from "../../components/Footer";

interface Props {
  isAuthenticated: boolean;
}

const Landing: React.FC<Props> = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const { showLoader } = useContext(LoaderContext);
  const { user } = useContext(AuthContext);
  const { setToken } = useToken();

  const verifyPayment = async (
    amountId: string,
    response: {
      razorpay_payment_id: string;
      razorpay_order_id: string;
      razorpay_signature: string;
    }
  ) => {
    const { data } = await api.CapturePayment(amountId, {
      ...response,
    });

    if (data.success) {
      showLoader({
        show: false,
      });
      setToken(data.data?.token);
      toast.success("Congratulations! your payment is successful!");
      return;
    }
    showLoader({
      show: false,
    });
    toast.error("Something went wrong!");
    return false;
  };

  const paymentHandler = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    amountId: string
  ) => {
    e?.preventDefault();
    await api
      .GetOrder(amountId)
      .then((response) => {
        const { data } = response.data;
        const options = {
          key: process.env.REACT_APP_RAZOR_PAY_KEY_ID,
          name: "WhatQR",
          amount: data.amount_due,
          order_id: data.id,
          handler: async (response: {
            razorpay_payment_id: string;
            razorpay_order_id: string;
            razorpay_signature: string;
          }) => {
            showLoader({
              show: true,
            });
            try {
              await verifyPayment(amountId, { ...response });
              showLoader({
                show: false,
              });
            } catch (err) {
              toast.error(
                "Something went wrong! You will get a refund in case of money loss"
              );
              showLoader({
                show: false,
              });
              console.log(err);
            }
          },
          theme: {
            color: "#000000",
          },
        };
        const rzp1 = new (
          window as Window & typeof globalThis & { Razorpay: any }
        ).Razorpay(options);

        rzp1.open();
      })
      .catch((err) => {
        console.log(err.response?.data?.message || "Something went wrong");
      });
  };

  console.log(user);

  useEffect(() => {
    var target = document.getElementById("typewriter-id");
    if (!target) {
      return;
    }
    var QrTypes = target?.getAttribute("data-type");
    if (!QrTypes) return;
    var QrList = JSON.parse(QrTypes);
    var count = 0;
    var index = 0;
    var currentType = "";
    var letter = "";
    (function type() {
      if (count === QrList.length) {
        count = 0;
      }
      currentType = QrList[count];
      letter = currentType.slice(0, index++);
      let wrapper = document.getElementById("wrap-id");
      if (!wrapper) return;
      wrapper.textContent = letter;
      if (index === currentType.length) {
        count++;
        index = 0;
      }
      if (index === 0) {
        setTimeout(type, 900);
      } else {
        setTimeout(type, 200);
      }
    })();
    let element = document.getElementById(
      `${window.location.hash.split("#")[1]}`
    )!;
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 200);
    }
  }, []);

  useEffect(() => {
    const demoItems = document.querySelectorAll(".demo-col");
    const fadeIns = document.querySelectorAll(".feature");

    const appearOnScroll = new IntersectionObserver(
      (entries, appearOnScroll) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("appear");
            appearOnScroll.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0,
        rootMargin: "0px 0px -100px 0px",
      }
    );

    demoItems.forEach((item) => {
      appearOnScroll.observe(item);
    });
    fadeIns.forEach((item) => {
      appearOnScroll.observe(item);
    });
  }, []);

  return (
    <>
      <Header title="WhatQR" />
      <StyledHeroSection>
        <div className="hero-section-text">
          <h1>
            Generate&nbsp;
            <span
              className="typewrite"
              id="typewriter-id"
              data-period="2000"
              data-type='[ "Text&nbsp;QRs ", "URL&nbsp;QRs ", "Phone&nbsp;QRs ", "SMS&nbsp;QRs ", "Email&nbsp;QRs ", "UPI&nbsp;QRs ", "WiFi&nbsp;QRs ", "Location&nbsp;QRs " ]'
            >
              <span className="wrap" id="wrap-id"></span>
            </span>
            <br />
            within seconds.
          </h1>
          <span className="hero-section-subtitle">
            Generate customized QR codes and short links for{" "}
            <strong>FREE</strong>.
          </span>
          <div className="hero-section-actions">
            <Button
              active
              label="Generate QR"
              onClick={() => {
                navigate("/generate/qr");
              }}
            />
            <Button
              active
              label="Generate short url"
              onClick={() => {
                if (isAuthenticated) {
                  navigate("/generate/url");
                } else {
                  toast.info("Log in or Register to generate a short url");
                  navigate("/login");
                }
              }}
            />
          </div>
        </div>

        <img src="/assets/imgs/_qr.svg" alt="QR Code" />

        <StyledSection></StyledSection>
      </StyledHeroSection>
      <StyledSectionTitle>
        <h1 className="first-header">
          How do I generate a <strong>FREE</strong> QR code?
        </h1>
        <span>You can in just three simple steps</span>
      </StyledSectionTitle>
      <DemoItem
        index={0}
        visuals="/assets/gifs/select_type.gif"
        title="Select your QR type"
        description="You may choose from URL, Plain Text, Email, SMS, Phone, Whatsapp, Location, WiFi, and UPI. These QR codes are FREE and completely editable."
      />
      <DemoItem
        index={1}
        visuals="assets/gifs/enter_info.gif"
        title="Fill in the required details"
        description="Enter all the information needed in the fields that appear. This could be a link, contact information, text or any other type of info. While you are entering you will see generation of QR code in real time. Once you’re done, select Generate to save the QR (you must be logged in to save)"
      />
      <DemoItem
        index={2}
        visuals="assets/gifs/print_qr.gif"
        title="Download or Print your QR"
        description="You may choose to have a standard black and white design or choose colors and frames to help you attract more scans. If not, proceed to download your finished Code."
      />
      <StyledSectionTitle>
        <h1 style={{ marginTop: 0 }}>Some of the features you get</h1>
        <span>
          You can also get access to the url shortener once you register for
          free.
        </span>
      </StyledSectionTitle>
      <StyledPrivacySection>
        <FeatureItem
          title="Maximum privacy"
          description="You select type, generate QR, print or download it without giving us any info about you."
          icon={<MdSecurity />}
        />
        <FeatureItem
          title="Custom QRs"
          description="Customize your QR code with choice of your colors and your company logo. Even edit the content after creation."
          icon={<MdDashboardCustomize />}
        />
        <FeatureItem
          title="Unlimited static QRs"
          description="We don't charge you for generating QR codes. You can generate unlimited static QR codes."
          icon={<ImInfinite />}
        />
        <span
          id="pricing_section"
          style={{
            position: "absolute",
            bottom: "36px",
          }}
        ></span>
      </StyledPrivacySection>

      <StyledSectionTitle>
        <h1>Subscription plans</h1>
        <span>
          lorium ipsum dolor sit amet, consectetur adipiscing elit, sed do
        </span>
      </StyledSectionTitle>

      <StyledPricingPlans>
        <PricingItem
          title={"6 Months"}
          subtitle={""}
          price={+(process.env.REACT_APP_SIX_MONTHS_AMOUNT! as string) / 100}
          originalPrice={400}
          active={false}
          options={[
            "Unlimited static QR generation",
            process.env.REACT_APP_SIX_MONTHS_QR_LIMIT + " QR storage",
            "Color and logo customization",
            "PNG, JPG, JPEG, and SVG download",
            process.env.REACT_APP_SIX_MONTHS_SHORT_URL_LIMIT +
              " short URL storage",
            "Custom short URL slug",
            "Variety of templates to print",
          ]}
          onSubscribe={(e) => {
            e.preventDefault();
            if (user?.currentPlan === "six_months") {
              toast.info("You are already subscribed to this plan");
              return;
            }
            if (!process.env.REACT_APP_SIX_MONTHS_AMOUNT_ID) return;
            paymentHandler(e, process.env.REACT_APP_SIX_MONTHS_AMOUNT_ID);
          }}
          subscribed={user?.currentPlan === "six_months"}
        />
        <PricingItem
          title={"12 Months"}
          active={true}
          subtitle={""}
          price={+(process.env.REACT_APP_TWELVE_MONTHS_AMOUNT! as string) / 100}
          originalPrice={1000}
          options={[
            "Unlimited static QR generation",
            process.env.REACT_APP_TWELVE_MONTHS_QR_LIMIT + " QR storage",
            "Color and logo customization",
            "PNG, JPG, JPEG, and SVG download",
            process.env.REACT_APP_TWELVE_MONTHS_SHORT_URL_LIMIT +
              " short URL storage",
            "Custom short URL slug",
            "Variety of templates to print",
          ]}
          onSubscribe={(e) => {
            e.preventDefault();
            if (user?.currentPlan === "twelve_months") {
              toast.info("You are already subscribed to this plan");
              return;
            }
            if (!process.env.REACT_APP_TWELVE_MONTHS_AMOUNT_ID) return;
            paymentHandler(e, process.env.REACT_APP_TWELVE_MONTHS_AMOUNT_ID);
          }}
          subscribed={user?.currentPlan === "twelve_months"}
        />

        <PricingItem
          title={"FREE"}
          subtitle={""}
          active={false}
          price={0}
          originalPrice={0}
          options={[
            "Unlimited static QR generation",
            process.env.REACT_APP_FREE_QR_LIMIT + " QR storage",
            "Color customization",
            "PNG, JPG, JPEG, and SVG download",
            process.env.REACT_APP_FREE_SHORT_URL_LIMIT + " short URL storage",
          ]}
          buttonText={"Already Subscribed"}
          onSubscribe={() => {
            toast.info("You already got it!");
          }}
          subscribed={user?.currentPlan === "free"}
        />
      </StyledPricingPlans>
      <Footer />
    </>
  );
};

export default Landing;
